import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TegelModule } from '@scania/tegel-angular-17';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { getPath, PathUnit } from '@/app/consts/path.const';
import { HeaderService } from './header.service';
import { RoleEnum, ROLE_DISPLAY_NAMES } from '@/app/consts/role.const';
import { environment } from '@/environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService, Language } from '@/app/core/services/language.service';
import { RoleTranslatePipe } from '@/app/core/pipes/role-translate.pipe';
import { LabelService } from '@/app/core/services/label.service';
import { RoleSwitcherService } from '@/app/core/services/role-switcher.service';
import { FullscreenService } from '@/app/core/services/fullscreen.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, TegelModule, TranslateModule, RoleTranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent {
  private authService = inject(AuthService);
  private headerService = inject(HeaderService);
  private readonly languageService = inject(LanguageService);
  private readonly labelService = inject(LabelService);
  private readonly fullscreenService = inject(FullscreenService);
  private readonly roleSwitcherService = inject(RoleSwitcherService);
  
  // 从 AuthService 获取角色管理
  availableRoles = this.roleSwitcherService.availableRoles;
  selectedRole = this.roleSwitcherService.selectedRole;
  
  // 获取角色显示名称
  getRoleDisplayName(role: RoleEnum): string {
    return ROLE_DISPLAY_NAMES[role] || role;
  }

  private readonly envName = environment.environmentName;
  selectedStorage$ = new BehaviorSubject<string>('');
  devStorageLabel = 'Development Storage';
  prodStorageLable = 'Production Storage';
  devStorageUrl = '/dev-storage';
  prodStorageUrl = '/prod-storage';
  activeHeaderItem = '';
  profilePic: string | null = null;
  default = 'assets/icons/default-profile.svg';
  profile = {
    name: 'User Name',
    email:"userName@email.com"
  };
  dashboardPath = getPath(PathUnit.Dashboard);
  mobileNavOpen = this.headerService.mobileNavOpen;
  readonly languageOptions = this.languageService.languageOptions;
  ngOnInit(){
    const userProfile = this.authService.getCurrentUserProfile();

    if (userProfile) {
      this.profile.name = userProfile.displayName;
      this.profile.email = userProfile.email;
    }

    // const languageSwitchEle = document.getElementById('languageSwitch');
    // if (languageSwitchEle) {
    //   languageSwitchEle.style.pointerEvents = 'none';
    //   languageSwitchEle.style.opacity = '0.3';
    // }
  }
  
  toggleMobileSideMenu() {
    this.headerService.toggleMobileSideMenu();
  }

  updateSelectedStorage(storage: string) {
    this.selectedStorage$.next(storage);
  }

  logout(){
    this.authService.fullLogout()
  }
  
  // 角色切换方法
  onRoleChange = this.roleSwitcherService.onRoleChange;

  getCurrentRole = this.selectedRole;
  
  // 检查是否有多个角色
  hasMultipleRoles = this.roleSwitcherService.hasMultipleRoles;

  isAdmin(): boolean {
    const currentRole = this.selectedRole();
    return currentRole === RoleEnum.Admin;
  }

  readonly currentLanguage = this.languageService.currentLanguage;

  getSingleRoleHint(): string {
    const roles = this.availableRoles();
    if (roles.length === 0) {
      return this.languageService.instant('header.singleRoleHintNone');
    }
    const roleLabel = this.getRoleDisplayName(roles[0]);
    return this.languageService.instant('header.singleRoleHint', { role: roleLabel });
  }

  setLanguage(language: Language) {
    this.languageService.setLanguage(language);
  }

  get environmentLabel(): string {
    const key = this.envName ?? 'development';
    return this.languageService.instant(`header.environment.${key}`);
  }

  readonly profileJobTitle = computed(() => {
    // Depend on both the selected language and the translation loader trigger.
    // When the app loads and translations finish loading (TranslateHttpLoader),
    // translationTrigger increments so this computed will re-evaluate and return
    // correctly-translated labels even if the language didn't change.
    this.languageService.currentLanguage();
    this.languageService.translationTrigger();
    const roles = this.availableRoles();
    if (!roles.length) {
      return this.languageService.instant('header.noRole');
    }
    return roles
      .map(role => this.labelService.getRoleLabel(role) || role)
      .join(', ');
  });

  

  readonly showFullscreenButton = computed(() => this.fullscreenService.isMobileOrTablet() || this.fullscreenService.isFullscreen());

  toggleFullscreen() {
    this.fullscreenService.toggleFullscreen();
  }
} 